import ExpertPageMobile from "@src/component/home/renewal/expert/mobile/ExpertPageMobile";
import ExpertPage from "@src/component/home/renewal/expert/pc/ExpertPage";
import { MetaTag } from "@src/utils/types/meta";
import type { NextPage, NextPageContext } from "next";
import Head from "next/head";
import { useEffect, useState } from "react";

const faqStructuredData = {
	"@context": "https://schema.org",
	"@type": "FAQPage",
	mainEntity: [
		{
			"@type": "Question",
			name: "현재 재직중인데 전문가로 활동할 수 있을까요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "가능합니다. 실제로 대다수의 원포인트 전문가분들이 현직자로서 원포인트 서비스를 수행하고 계십니다. 단, 현 직장에서 정해진 근무 시간이 있는 경우 해당 시간을 반드시 피해서 활동하셔야 합니다.",
			},
		},
		{
			"@type": "Question",
			name: "재직중인 혹은 재직했던 회사 이력을 마스킹 처리할 수 있을까요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "특정 이력에 대한 마스킹이 필요한 경우 프로필 작성 시 아래와 같이 입력할 수 있습니다.\n예) 롸잇 → 탑티어 마케터 단기 고용 서비스 O사\n단, 고객사는 전문가님의 프로필을 토대로 의사결정을 하기 때문에 마스킹을 하지 않거나 최소한으로 처리하시는 것을 추천드립니다.",
			},
		},
		{
			"@type": "Question",
			name: "어떤 직무로 전문가 지원이 가능한가요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "마케팅과 관련된 직무는 어떤 직무든 가능합니다. 퍼포먼스 마케터, 콘텐츠 마케터, 브랜드 마케터, CRM 마케터, 디자이너, 카피라이터가 대표적인 예입니다.",
			},
		},
		{
			"@type": "Question",
			name: "근무는 어떤 형태로 진행되나요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "대부분의 경우 비대면 형태로 업무하지만, 고객사 측에서 희망하는 경우 전문가와의 협의 하에 대면 또는 혼합 (대면+비대면) 근무로 진행됩니다. 업무 형태는 고객사의 업무 공고에서 확인할 수 있습니다.",
			},
		},
		{
			"@type": "Question",
			name: "고객사와의 매칭은 어떻게 진행되나요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "다양한 고객사의 업무 공고를 확인하고, 내가 원하는 곳에 원하는 만큼 지원할 수 있습니다. 사전에 프로필을 작성해두시면 빠르고 쉽게 지원할 수 있습니다.",
			},
		},
		{
			"@type": "Question",
			name: "매칭 이후 서비스 시작까지 어떤 절차를 거치나요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "고객사와의 Fit 검증을 위한 인터뷰 이후, 진행 의사가 있을시 제안서를 작성해주시면 고객사가 내용을 검토합니다. 고객사 최종 의사 결정까지는 보통 인터뷰 진행 후 영업일 기준 5일 정도 소요됩니다.",
			},
		},
	],
};

interface ProHomeProps extends MetaTag {
	isPc: boolean;
}

const ProHome: NextPage<ProHomeProps> = (props) => {
	const [isPc, setIsPc] = useState<boolean>(props.isPc);

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth >= 1024) {
				setIsPc(true);
			} else {
				setIsPc(false);
			}
		};
		window.addEventListener("resize", handleResize);

		handleResize();
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const structuredData = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: [
			{
				"@type": "ListItem",
				position: 1,
				name: "Home",
				item: "https://1point.kr",
			},
			{
				"@type": "ListItem",
				position: 2,
				name: "전문가",
				item: "https://1point.kr/expert/",
			},
		],
	};
	return (
		<>
			<Head>
				<link rel="canonical" href="https://1point.kr/expert/" />
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
				></script>

				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
				></script>
			</Head>
			{isPc ? <ExpertPage /> : <ExpertPageMobile />}
		</>
	);
};

ProHome.getInitialProps = async ({ req }: NextPageContext) => {
	let isPc = false;

	if (req) {
		// 서버 사이드에서 실행될 때
		const userAgent = req.headers["user-agent"];
		isPc = /Windows|Macintosh|Linux/i.test(userAgent!);
	} else {
		// 클라이언트 사이드에서 실행될 때
		const userAgent = navigator.userAgent;
		isPc = /Windows|Macintosh|Linux/i.test(userAgent);
	}
	const result = {
		meta: {
			title: "탑티어 마케터를 위한 프리랜서 플랫폼 - 원포인트",
			metaTitle: "탑티어 마케터를 위한 프리랜서 플랫폼 - 원포인트",
			metaDescription:
				"원포인트(One Point)는 업계 탑티어 마케터를 위한 프리랜서 플랫폼입니다.",
			metaOgType: "website",
			metaOgTitle: "탑티어 마케터를 위한 프리랜서 플랫폼 - 원포인트",
			metaOgDescription:
				"원포인트(One Point)는 업계 탑티어 마케터를 위한 프리랜서 플랫폼입니다.",
			metaOgUrl: "https://1point.kr/expert/",
			metaTwitterCard: "summary",
			metaTwitterTitle: "탑티어 마케터를 위한 프리랜서 플랫폼 - 원포인트",
			metaTwitterDescription:
				"원포인트(One Point)는 업계 탑티어 마케터를 위한 프리랜서 플랫폼입니다.",
		},
		isPc,
	};

	return result;
};

export default ProHome;
